const libPictView = require('pict-view');

/**
 * @typedef {import('../providers/ServiceCatalog.js').ServiceType} ServiceType
 */

/**
 * @typedef {Object} ServiceDisplayInfo
 * @property {string} Name - what we call the service or application
 * @property {string} PlatformVersion
 * @property {string} RepoLink
 * @property {ServiceType} Type
 */

class VersionsView extends libPictView
{
    constructor(pFable, pOptions, pServiceHash)
    {
        super(pFable, pOptions, pServiceHash);

        /** @type {import('../Application-Web').INTERNALSPict} */
        this.pict;

        this.reset();
    }

    /**
     * @return {import('../providers/ServiceCatalog.js')} The shared service inventory.
     */
    get serviceCatalog()
    {
        return this.pict.providers.InternalsServiceCatalog;
    }

    /**
     * @spec clears any data generated by this view. Useful for initialization and re-rendering
     */
    reset()
    {
        if (!this.AppData.Display)
        {
            this.AppData.Display = {};
        }
        this.AppData.Display.VersionGroups = [];
    }

    async buildModel()
    {
        const tmpEnvironmentKey = this.serviceCatalog.currentEnvironmentKey;
        const tmpInfos = await Promise.all(this.serviceCatalog.services.map(
            (pService) => this.serviceCatalog.fetchVersionInfoForService(pService, tmpEnvironmentKey)));
        const tmpInfoByService = new Map(this.serviceCatalog.services.map((pService, pIndex) => [ pService, tmpInfos[pIndex] ]));
        this.AppData.Display.VersionGroups = this.serviceCatalog.groups.map((pGroup) => (
            {
                Label: pGroup.Label,
                Services: this.serviceCatalog.servicesInGroup(pGroup.Key).map((pService) =>
                {
                    const tmpInfo = tmpInfoByService.get(pService);
                    const tmpVersion = (tmpInfo && tmpInfo.version) || '???';
                    // packageVersion is a services-only extra: apps omit it and a service that can't read its file
                    // reports '--' — show a badge only when there's a real, distinct package version.
                    const tmpPackageVersion = (tmpInfo && tmpInfo.packageVersion && tmpInfo.packageVersion !== '--') ? tmpInfo.packageVersion : '';
                    return {
                        Name: pService.Name,
                        Type: pService.Type,
                        PlatformVersion: tmpVersion,
                        PackageVersion: tmpPackageVersion,
                        RepoLink: this.serviceCatalog.buildHeadCommitURL(pService, tmpVersion),
                    };
                }),
            }));
    }

    async onBeforeRenderAsync(pCallback, pRenderable)
    {
        this.reset();
        await this.buildModel();
        super.onBeforeRenderAsync(pCallback, pRenderable);
    }
}

module.exports = VersionsView;
module.exports.default_configuration = (
    {
        ViewIdentifier: "ServiceVersions",
        DefaultRenderable: "ServiceVersions",
        DefaultDestinationAddress: "#PlatformConfiguration-Application-Container",

        AutoRender: true,

        CSS: /*css*/`
        .has-border-top-light {
            border-top: 1px dotted var(--bulma-border);
        }
        `,

        Templates: [
            {
                Hash: "VersionsView-Wrapper",
                Template: /*html*/`
                <section class="section">
                    <h1 class="title is-3 mb-1">Service Versions</h1>
                    <p class="subtitle is-6 has-text-text-50">The version each Headlight service and application currently reports in this environment. Click the branch icon to open the matching commit.</p>
                    <div class="columns is-multiline">
                        {~TS:VersionsView-Group:AppData.Display.VersionGroups~}
                    </div>
                </section>
                `
            },
            {
                Hash: "VersionsView-Group",
                Template: /*html*/`
                <div class="column is-6">
                    <section class="column card p-5">
                        <h2 class="title is-4 pb-4 mb-0 has-text-text-50">{~D:Record.Label~}</h2>
                        <div class="columns is-multiline">
                            {~TS:VersionsView-Service:Record.Services~}
                        </div>
                    </section>
                </div>
                `
            },
            {
                Hash: "VersionsView-Service",
                Template: /*html*/`
                <section class="column is-12 has-border-top-light">
                    <h2 class="title is-6">
                        {~D:Record.Name~}
                        <span class="pl-1 tag">{~D:Record.PlatformVersion~}</span>
                        {~TIfAbs:VersionsView-Service-PkgBadge:Record:Record.PackageVersion^>^0~}
                        {~TIfAbs:VersionsView-Service-RepoLink:Record:Record.RepoLink^>^0~}
                    </h2>
                </section>
                `
            },
            {
                Hash: "VersionsView-Service-PkgBadge",
                Template: /*html*/`
                <span class="pl-1 tag is-light" title="Service package version (package.json)">pkg {~D:Record.PackageVersion~}</span>
                `
            },
            {
                Hash: "VersionsView-Service-RepoLink",
                Template: /*html*/`
                <a href="{~D:Record.RepoLink~}" target="_blank" title="Click to see the code of this version">
                    <i class="fa-regular fa-code-branch"></i>
                </a>
                `
            },
        ],
        Renderables: [
            {
                RenderableHash: "ServiceVersions",
                TemplateHash: "VersionsView-Wrapper",
                RenderMethod: 'replace'
            }]
    });
